import {
  isPayPalSupported,
  isVenmoSupported,
} from 'components/payments/integrations/firstdata/braintree.config';
import { AllLaunchDarklyFlags } from 'state/launchdarkly/flags';
import { isUS, isWeb, region } from 'utils/environment';
import { Region } from 'utils/environment/types';

// NOTE IMPORTANT!!
// These flags should be updated over time to whatever the normal prod behavior is and they will be used in special situations:
// 1) early app start the first time the app is ever launched (no cached values available) before network fetch
// 2) continue to be used if no cached values are available and they can't be fetched from network
const safeFlagDefaults: AllLaunchDarklyFlags = {
  'enable-vault-after-making-one-time-payment': false,
  'enable-test-apple-pay-web': false,
  'enable-test-google-pay-web': false,
  'enable-ads-personalization-opt-out': false,
  'enable-paypal-payments': isPayPalSupported(),
  'enable-venmo-payments': isVenmoSupported(),
  'enable-cash-app-payments': false,
  'show-ordering-issue-info': true,
  'show-increased-delivery-fees-modal': false,
  'sanity-project-id': 'kjfd81ul',
  'app-takeover':
    region === Region.CA
      ? [
          {
            name: 'elevation',
            startDate: '2026-03-31T00:00:00-05:00',
            endDate: '2026-05-03T23:59:59-05:00',
          },
          {
            name: 'mandalorian',
            startDate: '2026-05-04T00:00:00-05:00',
            endDate: '2026-06-04T23:59:59-05:00',
          },
        ]
      : [
          {
            name: 'mandalorian',
            startDate: '2026-05-04T00:00:00-05:00',
            endDate: '2026-06-04T23:59:59-05:00',
          },
        ],
  tipPercentThresholdCents: 1500,
  enableSignUp: true,
  'send-postal-code-only-for-first-data-payment': false,
  'enable-delivery-first-quote-v2': true,
  enableOffers: true,
  'enable-cash-payment': false,
  'skip-loyalty-store-validation': false,
  enableApplePay: true,
  enableAutoSelectRestaurant: true,
  'enable-sort-offers-for-service-mode': true,
  'enable-delivery-details-form-require-address-2': false,
  enableInternalDiagnosticTools: false,
  disableDriveThru: false,
  disableTakeOut: false,
  'enable-feature-onboarding': !isWeb,
  autoFillTestCard: false,
  'order-locator-tabs': { '0': 'nearby', '1': 'favorite', '2': 'recent' },
  'enable-find-restaurant-link': true,
  'enable-loyalty-fe': true,
  'enable-nutrition-explorer-allergens': false,
  'enable-add-extras': true,
  'enable-zero-bounce-error-modal': true,
  'enable-locator-clear-store-and-sm-button': false,
  'enable-sodium-warning': false,
  'reset-cart-timeout-hrs': 48,
  'tiered-delivery-fees': {
    '0': {
      googleFoodOrdering: { fee: 100, discount: 0, smallCartFee: 250, serviceFeePercent: 15 },
      app: { discount: 0, serviceFeePercent: 15, smallCartFee: 250, fee: 100 },
      web: { discount: 0, fee: 100, smallCartFee: 250, serviceFeePercent: 15 },
      kiosk: { discount: 0, fee: 0, serviceFeePercent: 0, smallCartFee: 0 },
    },
    '1000': {
      app: { fee: 100, discount: 0, smallCartFee: 0, serviceFeePercent: 15 },
      web: { fee: 100, discount: 0, smallCartFee: 0, serviceFeePercent: 15 },
      kiosk: { discount: 0, fee: 0, serviceFeePercent: 0, smallCartFee: 0 },
      googleFoodOrdering: { discount: 0, fee: 100, serviceFeePercent: 15, smallCartFee: 0 },
    },
  },
  enableStoreConfirmationModal: true,
  'enable-loyalty-points-in-header': false,
  'enable-payment-error-continue': true,
  'supported-card-brands-variations': ['amex', 'diners', 'discover', 'jcb', 'mc', 'visa'],
  'enable-menu-reordering-availability-override': [],
  'store-card-button-options': { '0': 'info', '1': 'order', '2': 'offers' },
  'enable-loyalty-celebrations': false,
  'enable-doordash-drive-address-suggestions': false,
  'enable-loyalty-profile': false,
  'enable-loyalty-promo-codes': true,
  'enable-pypestream': region !== Region.CA,
  'enable-dayparts-per-store': false,
  fireOrderAhead: 240,
  'enable-loyalty-standard-offers': true,
  'enable-discounts-on-order-receipt': false,
  'enable-order-number-generation': false,
  'radius-for-store-locator': 32000,
  'enable-global-offers-cooldown': true,
  'enable-edit-offers-cart-button': false,
  'enable-my-code-payment-method': false,
  'hide-items-from-nutrition-info': {
    proteins: false,
    cholesterol: false,
    calories: false,
    saltPer100: false,
    energyKJPer100: false,
    saturatedFat: false,
    carbohydrates: false,
    sodium: false,
    fatPer100: false,
    caloriesPer100: false,
    sugarPer100: false,
    sugar: false,
    carbohydratesPer100: false,
    proteinsPer100: false,
    fat: false,
    salt: false,
    saturatedFatPer100: false,
    transFat: false,
    fiber: false,
    weight: true,
    energyKJ: false,
  },
  enableAccountDeleteOneTrust: true,
  enableFreeDeliveryCheckoutProgressBar: false,
  'enable-reorder': true,
  enableGooglePay: true,
  'enable-qr-code-loyalty-id': false,
  'enable-offer-expiration-display': false,
  enableCustomBirthdayInput: true,
  defaultServiceModeInStore: 'DISABLED',
  'enable-forced-native-app-update': 'none',
  'payment-fields-variations': {
    zip: true,
    state: false,
    country: false,
    name: true,
    addressLine1: false,
    city: false,
    expiration: true,
    cardNumber: true,
    cvv: true,
    addressLine2: false,
  },
  'enable-fees-on-order-receipt': false,
  curbsideVehicleDescriptionMaxLength: 17,
  'loyalty-in-restaurant-status-poll-interval-ms': 3000,
  resetStoreResetCartTimeout: 0,
  'enable-in-restaurant-variations': { enableRewardRedemption: true, enableOfferRedemption: true },
  'force-restaurant-selection-for-rewards': true,
  deliveryRadiusInMiles: 6,
  delivery: true,
  'enable-loyalty-offers': true,
  'enable-price-mismatch-internal-only': false,
  'sign-up-fields-variations': {
    email: true,
    dob: true,
    zip: false,
    country: false,
    phone: false,
    name: true,
    wantsPromotionalEmails: true,
    agreesToTermsOfService: true,
    gender: false,
  },
  'enable-signup-cart-button-text': false,
  'enable-forter-fraud': true,
  'enable-offers-static-page': false,
  checkoutDeliveryMinimumOverrideCents: 500,
  'enable-offers-edit-cart': false,
  'enable-claim-loyalty-points': true,
  enableCognitoSignupInBe: true,
  'enable-prepaid-payment-methods': {
    ONE_TIME_METHOD: true,
    GOOGLE_PAY: true,
    EXISTING_METHOD: true,
    CREDIT: true,
    APPLE_PAY: true,
  },
  'enable-quest-tiles': true,
  'enable-move-signup-after-cart': false,
  disableDineIn: false,
  'gift-card-payment-method': true,
  'enable-cooldown-toast-info': false,
  enableAllergensDisclaimer: false,
  'enable-main-fab-button': true,
  'enable-foodware-items': false,
  'max-rbi-api-retries': 5,
  'max-sanity-api-retries': 10,
  'enable-fast-lane-drive-thru': false,
  'enable-optional-amplitude-tracking': false,
  'enable-debug-menu': false,
  'product-details-force-picker-image': false,
  'enable-mobile-order-drive-thru-lane': false,
  'enable-sms-marketing-subscriptions': false,
  'enable-unlink-walmart-flow': false,
  'enable-apple-pay-web': false,
  'enable-id-and-pay-fe': false,
  'enable-apple-mapkit-web': false,
  'enable-social-login-google': true,
  'enable-social-login-apple': true,
  'enable-top-order-status-bar': false,
  'order-injection-flow': 'INJECT_NOW',
  'enable-foundation-donations': false,
  'enable-walmart-plus-integration': isUS,
  'enabled-otp-login': true,
  'delivery-fees-stripe': 'disabled',
  'user-away-from-store': 0,
  'enable-non-traditional-store-ui': false,
  'enable-ios-live-activity': false,
  'enable-cart-cancellation-policy': false,
  'commit-order-test-error-modal': '',
  'enable-walmart-plus-trial-offer': false,
  'enable-walmart-plus-trial-offer-monthly': false,
  'enable-auto-select-closest-store-prompt': false,
  'enable-google-pay-web': false,
  'enable-account-new-badge': '',
  'top-order-status-bar-time-out': 120,
  'auto-select-store-prompt-timeout': 0,
  'enable-webview-debugging': false,
  'enable-mobile-drive-thru-service-mode': false,
  'enable-smart-alerts': false,
  'mobile-order-drive-thru-stores': '',
  'nyc-regulatory-link': '',
  'show-braze-content-cards-on-homepage': region === Region.US,
  'enable-feature-item-nutrition-ingredients': false,
  'show-walmart-seasonal-promo': false,
  'enable-offer-groups-v2': false,
  'enable-loyalty-personalized-offer-groups': false,
  'enable-rewards-stacking-evaluation': false,
  'enable-rewards-cooldown-evaluation': false,
  'enable-loyalty-badges': false,
  'enable-profile-completion-challenge': false,
  'show-mastercard-partnership': false,
  'show-spotify-partnership': false,
  'is-digital-gift-card-number-exposed-enabled': false,
  'enable-loyalty-service-offers': false,
};

export default safeFlagDefaults;
